import { replaceTokens } from '@sleepstation/utils'
import PropTypes from 'prop-types'
import { flip, identity, mergeRight, over } from 'ramda'
import lensProp from 'ramda/src/lensProp'
import pipe from 'ramda/src/pipe'
import React from 'react'
import PageHeader from './PageHeader'
import ResultSection from './ResultSection'
import SleepAndEmotionsSection from './SleepAndEmotionsSection'
import SleepEfficencyScoreSection from './SleepEfficencyScoreSection'
import YourSleepPatternsSection from './YourSleepPatternsSection'

// -----------------------------------------------------------------------------
// Utils
// -----------------------------------------------------------------------------

const replaceTokensFlipped = flip(replaceTokens)
const pathTitle = lensProp(`title`)
const overTitle = over(pathTitle)

const renderComponent = (
  Component,
  props = {},
  { transformers = [identity], additionalData = {} } = {}
) => {
  const transformedProps = pipe(
    ...transformers,
    mergeRight(additionalData)
  )(props)
  return <Component {...transformedProps} />
}

// -----------------------------------------------------------------------------
// Factories
// -----------------------------------------------------------------------------

const buildHeader = (Component, { report, content, user }) => {
  const { displayName } = user

  const injectUserName = replaceTokensFlipped({
    displayName,
  })

  const injectUserNameIntoTitle = overTitle(injectUserName)

  const {
    seenByEnrolment,
    result: { action, forceAccepted },
  } = report

  const { header } = content
  const { backLink, titleTemplate, forceAcceptedParagraph } = header

  const reportSeenAndForceAccepted = forceAccepted && seenByEnrolment

  const title = reportSeenAndForceAccepted
    ? titleTemplate.forceAccepted
    : titleTemplate.default

  const data = {
    backLink,
    title,
    seenByEnrolment,
    forceAccepted,
    forceAcceptedParagraph,
    action,
  }

  return renderComponent(Component, data, {
    transformers: [injectUserNameIntoTitle],
  })
}

const buildSleepEfficiencySection = (Component, { report }) => {
  const { sleepEfficiencyScale } = report

  return renderComponent(Component, {
    data: sleepEfficiencyScale,
  })
}

const buildSleepPatternsSection = (Component, { report, content, user }) => {
  const { sleepPattern } = report
  const { yourSleepPatternsSection } = content

  const data = mergeRight(yourSleepPatternsSection, { sleepPattern, user })

  return renderComponent(Component, {
    data,
  })
}

const buildSleepAndEmotionsSection = (Component, { report, content }) => {
  const { anxietyScale, depressionScale, stressScale } = report
  const { sleepAndEmotionsSection } = content

  const data = {
    ...sleepAndEmotionsSection,
    anxietyScale,
    depressionScale,
    stressScale,
  }

  return renderComponent(Component, {
    data,
  })
}

const buildResultsSection = (Component, { report, content }) => {
  const { result, considerations, enrolment } = report
  const { resultsSection } = content

  const data = {
    result,
    considerations,
    enrolment,
    ...resultsSection,
  }

  return renderComponent(Component, {
    data,
  })
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const SleepReport = ({ currentSleepReport, content }) => {
  const {
    enrolment: { user },
  } = currentSleepReport

  return (
    <React.Fragment>
      {buildHeader(PageHeader, {
        report: currentSleepReport,
        content,
        user,
      })}

      {buildSleepEfficiencySection(SleepEfficencyScoreSection, {
        report: currentSleepReport,
        content,
      })}

      {buildSleepPatternsSection(YourSleepPatternsSection, {
        report: currentSleepReport,
        content,
        user,
      })}

      {buildSleepAndEmotionsSection(SleepAndEmotionsSection, {
        report: currentSleepReport,
        content,
      })}

      {buildResultsSection(ResultSection, {
        report: currentSleepReport,
        content,
      })}
    </React.Fragment>
  )
}

SleepReport.propTypes = {
  currentSleepReport: PropTypes.object.isRequired,
  content: PropTypes.object.isRequired,
}

export default SleepReport
